// Shared load-error component.
//
// Rendered by `/s/:slug/+Page.tsx` when the backend fetch fails with a
// 5xx or network error (distinct from 404 which uses <NotFound/>).
// Provides a retry button that re-fetches by reloading the page —
// simpler than a stateful client-side retry and works with the vike
// SSR data-loader pattern in `+data.ts`.

import type { ReactNode } from "react";
import { AppShell } from "../layout/AppShell";
import { FONT_DISPLAY, PINK, VARIANT } from "../../lib/tokens";
import { useVariant } from "../../hooks/useVariant";

export interface LoadErrorProps {
  headline?: ReactNode;
  detail?: ReactNode;
  eyebrow?: string;
  /** Slug the request failed for — shown in the detail line for debugging. */
  slug?: string;
}

export function LoadError({
  headline,
  detail,
  eyebrow = "Fehler beim Laden",
  slug,
}: LoadErrorProps) {
  const { variant } = useVariant();
  const v = VARIANT[variant];

  const handleRetry = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  return (
    <AppShell currentNavId="" loggedIn={false}>
      <div
        style={{
          padding: "120px 40px",
          textAlign: "center",
          maxWidth: 640,
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "inline-block",
            fontSize: 12,
            fontWeight: 600,
            color: PINK,
            letterSpacing: 1,
            textTransform: "uppercase",
            marginBottom: 10,
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            fontFamily: FONT_DISPLAY,
            fontSize: 56,
            fontWeight: 500,
            color: v.text,
            margin: 0,
            letterSpacing: -1.2,
            lineHeight: 1.0,
          }}
        >
          {headline ?? (
            <>
              Der Scrolly <em style={{ color: PINK, fontStyle: "italic" }}>ließ sich nicht laden</em>.
            </>
          )}
        </h1>
        <p
          style={{
            color: v.muted,
            fontSize: 16,
            lineHeight: 1.5,
            marginTop: 24,
          }}
        >
          {detail ??
            (slug
              ? `Das Backend hat den Scrolly "/${slug}" nicht zurückgegeben. Bitte erneut versuchen.`
              : "Das Backend hat nicht geantwortet. Bitte erneut versuchen.")}
        </p>
        <div style={{ marginTop: 24, display: "flex", justifyContent: "center", gap: 12 }}>
          <button
            onClick={handleRetry}
            type="button"
            style={{
              background: PINK,
              color: "#fff",
              padding: "11px 24px",
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
            }}
          >
            Erneut versuchen
          </button>
          <a
            href="/"
            style={{
              display: "inline-block",
              background: "transparent",
              color: v.text,
              padding: "11px 24px",
              borderRadius: 999,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: "none",
              border: `1px solid ${v.border}`,
            }}
          >
            Zur Startseite
          </a>
        </div>
      </div>
    </AppShell>
  );
}
